import {
  Button,
  Card,
  HStack,
  Input,
  InputGroup,
  Select,
  Stack,
  Text,
  VStack,
  IconButton,
  Tooltip,
  InputRightElement,
  Switch,
} from '@chakra-ui/react';
import { FC, useEffect, useMemo, useState } from 'react';
import { FiRefreshCcw, FiEye, FiEyeOff, FiCheck } from 'react-icons/fi';
import useFetch, { IncomingOptions } from 'use-http';
import { useNavigate, useParams } from 'react-router';
import { UserRole, UserStatus, getUserTypeLabel } from '../../util/user';
import Loading from '../Misc/Loading';
import { api, makeHeaders } from '../../util/api';
import { generatePassword } from '../../util/string';
import HeadingSection from '../HeadingSection';
import { useNotify } from '../../hooks/useNotify';

export const UserEditor: FC = () => {
  const { id } = useParams();
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [role, setRole] = useState<UserRole>(UserRole.COLLABORATOR);
  const [status, setStatus] = useState<string>(UserStatus.ACTIVE);
  const [phone, setPhone] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const notify = useNotify()
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(true);
  const toggleShowPassword = () => setShowPassword(!showPassword);

  const toggleStatus = () => {
    setStatus(status === UserStatus.ACTIVE ? UserStatus.DISABLED : UserStatus.ACTIVE);
  };

  const createPasswordHandler = () => {
    const newPassword = generatePassword();
    setPassword(newPassword);
  };

  const passwordChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const {
    get: getUser,
    post: createUser,
    patch: updateUser,
    cache,
    loading,
    response,
  } = useFetch(`${api}/users`, makeHeaders() as IncomingOptions);

  const saveHandler = async () => {
    if (id) {
      await updateUserHandler();
    } else {
      await createUserHandler();
    }
  };

  const createUserHandler = async () => {
    try {
      cache.clear();
      await createUser({
        firstName,
        lastName,
        email,
        role,
        phone,
        password,
      });

      if (response.ok) {
        notify('Colaborador agregado', 'success');
        navigate(-1);
      } else {
        throw new Error();
      }
    } catch {
      notify('Error al agregar colaborador', 'error');
    }
  };
  const updateUserHandler = async () => {
    try {
      cache.clear();
      await updateUser(id, {
        firstName,
        lastName,
        email,
        role,
        status,
        phone,
        password,
      });

      if (response.ok) {
        notify('Colaborador actualizado', 'success');
      } else {
        throw new Error();
      }
    } catch (error) {
      notify('Error al actualizar colaborador', 'error');
    }
  };

  const fetchUser = async () => {
    cache.clear();
    const user = await getUser(id);

    if (response.ok) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setEmail(user.email);
      setRole(user.role);
      setStatus(user.status);
      setPhone(user.phone);
    }
  };

  useEffect(() => {
    fetchUser(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = id ? 'Editar colaborador' : 'Agregar colaborador';
  const hideStatus = !id;
  const disableSave = useMemo(() => {
    if (!id) {
      return !firstName || !lastName || !email || !role || !password || !phone;
    }

    return false;
  }, [id, firstName, lastName, email, role, phone, password]);

  return (
    <Stack maxW='container.sm'>
      <Card p={4} mb={2} variant='outline'>
        <HeadingSection title={title} />
        <Loading isLoading={loading} dense />
        <HStack gap={4}>
          <VStack align='start' w='full' mb={6} gap={1}>
            <Text fontSize='sm' fontWeight='semibold'>Nombre</Text>
            <Input
              placeholder='Nombre'
              defaultValue={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
              }}
            />
          </VStack>

          <VStack align='start' w='full' mb={6} gap={1}>
            <Text fontSize='sm' fontWeight='semibold'>Apellido</Text>
            <Input
              placeholder='Apellido'
              defaultValue={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
              }}
            />
          </VStack>
        </HStack>

        <HStack gap={4}>
          <VStack align='start' w='full' mb={6} gap={1}>
            <Text fontSize='sm' fontWeight='semibold'>Teléfono</Text>
            <Input
              placeholder='Teléfono'
              defaultValue={phone}
              onChange={(e) => {
                setPhone(e.target.value);
              }}
            />
          </VStack>

          <VStack align='start' w='full' mb={6} gap={1}>
            <Text fontSize='sm' fontWeight='semibold'>Correo electrónico</Text>
            <Input
              placeholder='Correo electrónico'
              defaultValue={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </VStack>
        </HStack>

        <HStack gap={4}>
          {!hideStatus && (
            <VStack align='start' w='full' mb={6} gap={1}>
              <Text fontSize='sm' fontWeight='semibold'>Función</Text>
              <Select
                value={role}
                onChange={(e) => {
                  setRole(e.target.value as UserRole);
                }}
              >
                {Object.values(UserRole).map((role) => (
                  <option key={role} value={role}>{getUserTypeLabel(role)}</option>
                ))}
              </Select>
            </VStack>
          )}

          <VStack align='start' w='full' mb={6} gap={1}>
            <Text fontSize='sm' fontWeight='semibold'>Contraseña</Text>
            <InputGroup size='md'>
              <Input
                pr='4.5rem'
                type={showPassword ? 'text' : 'password'}
                placeholder='Escribe una contraseña'
                value={password}
                onChange={passwordChangeHandler}
              />
              <InputRightElement width='4.5rem'>
                <Tooltip label='Generar contraseña'>
                  <IconButton
                    isRound
                    size='sm'
                    aria-label='new-password'
                    onClick={createPasswordHandler}
                    variant='ghost'
                  >
                    <FiRefreshCcw />
                  </IconButton>
                </Tooltip>

                <Tooltip label={showPassword ? 'Ocultar' : 'Mostrar'}>
                  <IconButton
                    isRound
                    size='sm'
                    aria-label='show password'
                    onClick={toggleShowPassword}
                    variant='ghost'
                  >
                    {showPassword ? <FiEye /> : <FiEyeOff />}
                  </IconButton>
                </Tooltip>
              </InputRightElement>
            </InputGroup>
          </VStack>
        </HStack>
        <HStack justifyContent={hideStatus ? 'end' : 'space-between'}>
          {!hideStatus && (
            <HStack justify='end'>
              <Text fontWeight='bold' color='gray.500' textTransform='uppercase' fontSize='xs'>Activo</Text>
              <Switch
                isChecked={status === UserStatus.ACTIVE}
                onChange={toggleStatus}
              />
            </HStack>
          )}
          <Button
            colorScheme='brand'
            variant='solid'
            size='sm'
            isDisabled={loading || disableSave}
            isLoading={loading}
            leftIcon={<FiCheck />}
            onClick={saveHandler}
          >
            Guardar
          </Button>
        </HStack>
      </Card>
    </Stack>
  );
};
